/**
 * Aplica route_path de Kimi (p. ej. /invitacion-premium) una vez por sesión.
 */

import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { isPostHogConfigured } from "@/lib/posthogAnalytics";
import { grantGalleryPremiumRouteAccess } from "@/lib/premiumInviteAccess";
import type { ConversionControl } from "@/lib/visitorPurchaseEvalStorage";

const ROUTED_KEY = "ph_conversion_route_applied_v1";

/** Landings donde el orquestador puede redirigir a /invitacion-premium. */
const HOME_PATHS = new Set([
  "/",
  "/ar",
  "/index.html",
  "/video-landing",
  "/argentina",
]);

export function useConversionControlNavigation(
  control: ConversionControl,
  opts?: { enabled?: boolean; countryCode?: string },
): void {
  const navigate = useNavigate();
  const appliedRef = useRef<string | null>(null);

  useEffect(() => {
    if (opts?.enabled === false) return;
    const target = control.navigateTo?.trim();
    if (!target) return;
    if (typeof window === "undefined") return;

    const current = window.location.pathname;
    if (current === target) return;
    if (!HOME_PATHS.has(current) && !current.startsWith("/?")) return;

    if (target === "/invitacion-premium") {
      grantGalleryPremiumRouteAccess();
    }

    const dedupeKey = `${target}`;
    if (appliedRef.current === dedupeKey) return;
    try {
      if (sessionStorage.getItem(ROUTED_KEY) === dedupeKey) return;
      sessionStorage.setItem(ROUTED_KEY, dedupeKey);
    } catch {
      /* */
    }
    appliedRef.current = dedupeKey;

    if (isPostHogConfigured()) {
      try {
        window.posthog.capture("conversion_control_navigate", {
          site: "delfuocampo",
          from_path: current,
          to_path: target,
          country: opts?.countryCode,
          control_surface: control.surface,
        });
      } catch {
        /* */
      }
    }

    navigate(target, { replace: false });
  }, [
    control.navigateTo,
    control.surface,
    navigate,
    opts?.enabled,
    opts?.countryCode,
  ]);
}
